import { useState } from "react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { EnvelopeSimple, PaperPlaneTilt, CheckCircle, BookOpen, ArrowRight } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";

const SUBJECTS = [
  "Bug report",
  "Feature request",
  "Question / help",
  "Press / partnership",
  "Other",
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: SUBJECTS[2], message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Fill in name, email, and message");
      return;
    }
    if (form.message.trim().length < 10) {
      toast.error("Add a few more details — at least 10 characters please.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Sent — we'll reply within 48h");
      setForm({ name: "", email: "", subject: SUBJECTS[2], message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't send — try again");
    } finally {
      setSending(false);
    }
  };

  return (
    <AppShell title="Help & Contact">
      <p className="text-[var(--brand-muted)] mb-8 max-w-2xl">
        the JobVault team reads every message. bugs, feature ideas, partnership chats — all welcome.
        replies usually land within 48 hours.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        <form onSubmit={submit} className="card-hard rounded-md p-6 bg-white" data-testid="contact-form">
          {sent ? (
            <div className="text-center py-10" data-testid="contact-success">
              <CheckCircle size={48} weight="duotone" className="mx-auto text-[var(--bolt-sage)]"/>
              <p className="font-display text-2xl tracking-tight mt-3">message in the vault.</p>
              <p className="text-sm text-[var(--brand-muted)] mt-2">we&apos;ll reply to your email within 48 hours.</p>
              <button type="button" onClick={() => setSent(false)} data-testid="contact-send-another"
                className="mt-5 btn-hard bg-white rounded-md px-4 py-2 text-sm">
                send another
              </button>
            </div>
          ) : (
            <>
              <p className="label-tiny mb-4">/your details</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="label-tiny block mb-1.5">name</label>
                  <input data-testid="contact-name" value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="your name"
                    className="w-full card-hard rounded-md px-3 py-2 bg-white"/>
                </div>
                <div>
                  <label className="label-tiny block mb-1.5">email</label>
                  <input data-testid="contact-email" type="email" value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="you@example.com"
                    className="w-full card-hard rounded-md px-3 py-2 bg-white"/>
                </div>
              </div>
              <label className="label-tiny block mb-1.5">subject</label>
              <select data-testid="contact-subject" value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                className="w-full card-hard rounded-md px-3 py-2 bg-white mb-3">
                {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <label className="label-tiny block mb-1.5">message</label>
              <textarea data-testid="contact-message" value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="what's up? bug? idea? feedback?"
                className="w-full card-hard rounded-md px-3 py-3 bg-white min-h-[180px] text-sm mb-4"/>
              <button type="submit" disabled={sending} data-testid="contact-submit"
                className="btn-hard bg-[var(--brand-primary)] text-white rounded-md px-4 py-2.5 text-sm flex items-center gap-2 disabled:opacity-60">
                <PaperPlaneTilt size={16} weight="bold"/> {sending ? "sending…" : "send message"}
              </button>
            </>
          )}
        </form>

        <div className="space-y-4">
          <div className="card-hard rounded-md p-5 bg-[var(--pastel-yellow)]">
            <p className="label-tiny mb-2 flex items-center gap-1.5">
              <EnvelopeSimple size={12} weight="duotone"/> /direct email
            </p>
            <p className="text-sm">prefer plain email?</p>
            <a href="mailto:jobvaultadmin@gmail.com" data-testid="contact-direct-email"
              className="mt-2 font-mono-d text-sm underline break-all">
              jobvaultadmin@gmail.com
            </a>
          </div>

          <div className="card-hard rounded-md p-5 bg-[var(--pastel-mint)]">
            <p className="label-tiny mb-2 flex items-center gap-1.5">
              <BookOpen size={12} weight="duotone"/> /self-serve
            </p>
            <p className="text-sm mb-3">most questions are answered in the guide.</p>
            <Link to="/guide" data-testid="contact-guide-link"
              className="btn-hard bg-white rounded-md px-3 py-2 text-xs flex items-center gap-1.5 inline-flex">
              open the guide <ArrowRight size={12} weight="bold"/>
            </Link>
          </div>

          <div className="card-hard rounded-md p-5 bg-white">
            <p className="label-tiny mb-2">/response times</p>
            <ul className="text-sm space-y-1 text-[var(--brand-muted)]">
              <li>· bug reports: same day</li>
              <li>· feature requests: 48h</li>
              <li>· press / partnerships: 3-5 days</li>
            </ul>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
